import type { RecordDocument, RepeatExpenseDocument } from '../services/firestoreService';

export interface CashflowDay { day: number; income: number; expense: number; }
export interface CashflowMarker { day: number; label: string; amount: number; kind: 'payday' | 'repeat'; }
export interface CashflowMonth {
  days: CashflowDay[];
  markers: CashflowMarker[];
  totals: { income: number; expense: number; net: number };
}
export interface PaydayOutflow { day: number; amount: number; }

export function isExpenseType(incomeType: string): boolean {
  return incomeType === '지출' || incomeType === 'expense';
}
function isIncomeType(incomeType: string): boolean {
  return incomeType === '매출' || incomeType === 'income';
}

/** RecordDocument 의 실제 날짜(Timestamp) 추출. createdTime 우선, date fallback. */
function recordDate(r: RecordDocument): Date | null {
  const ts = r.createdTime ?? r.date;
  if (ts && typeof (ts as { toDate?: unknown }).toDate === 'function') return (ts as { toDate: () => Date }).toDate();
  if (typeof r.createTimeMilli === 'number') return new Date(r.createTimeMilli);
  return null;
}

/** 반복거래 예정 일자 추정: 매칭 과거 거래 최빈 일자 → 없으면 생성일 일자. */
export function inferRepeatDay(repeat: RepeatExpenseDocument, records: RecordDocument[]): number {
  const matches = records.filter((r) => {
    if (r.name !== repeat.name) return false;
    if (repeat.merchantName && r.merchantName !== repeat.merchantName) return false;
    return true;
  });
  if (matches.length > 0) {
    const freq = new Map<number, number>();
    for (const m of matches) {
      const d = recordDate(m);
      if (!d) continue;
      const day = d.getDate();
      freq.set(day, (freq.get(day) ?? 0) + 1);
    }
    if (freq.size > 0) {
      let bestDay = 1;
      let bestCount = -1;
      for (const [day, count] of freq) {
        if (count > bestCount || (count === bestCount && day > bestDay)) {
          bestDay = day;
          bestCount = count;
        }
      }
      return bestDay;
    }
  }
  return new Date(repeat.createTimeMilli).getDate();
}

/** 선택한 달의 일별 수입/지출 + 예정 마커(급여·추정 반복거래) 산출. */
export function buildCashflowMonth(params: {
  records: RecordDocument[];
  repeats: RepeatExpenseDocument[];
  paydayOutflows: PaydayOutflow[];
  year: number;
  month: number;
  today: Date;
}): CashflowMonth {
  const { records, repeats, paydayOutflows, year, month, today } = params;

  const dayMap = new Map<number, CashflowDay>();
  let totalIncome = 0;
  let totalExpense = 0;

  for (const r of records) {
    const d = recordDate(r);
    if (!d || d.getFullYear() !== year || d.getMonth() + 1 !== month) continue;
    const day = d.getDate();
    const entry = dayMap.get(day) ?? { day, income: 0, expense: 0 };
    const amount = r.money ?? r.amount ?? 0;
    if (isExpenseType(r.incomeType ?? r.type ?? '')) {
      entry.expense += amount;
      totalExpense += amount;
    } else if (isIncomeType(r.incomeType ?? r.type ?? '')) {
      entry.income += amount;
      totalIncome += amount;
    }
    dayMap.set(day, entry);
  }

  const markers: CashflowMarker[] = [];
  const todayDay = today.getDate();
  const isFuture = (day: number): boolean => {
    if (year !== today.getFullYear()) return year > today.getFullYear();
    if (month !== today.getMonth() + 1) return month > today.getMonth() + 1;
    return day > todayDay;
  };

  // 급여 지급일 마커 (미래만)
  for (const p of paydayOutflows) {
    if (p.amount > 0 && isFuture(p.day)) {
      markers.push({ day: p.day, label: '예상 인건비', amount: p.amount, kind: 'payday' });
    }
  }

  // 반복거래 추정 마커 (지출·비-placeholder·미래·중복 제외)
  const namesWithActual = new Set(
    records.filter((r) => {
      const d = recordDate(r);
      return d && d.getFullYear() === year && d.getMonth() + 1 === month;
    }).map((r) => r.name),
  );
  for (const rp of repeats) {
    if (rp.isPlaceholder) continue;
    if (!isExpenseType(rp.incomeType)) continue;
    if (namesWithActual.has(rp.name)) continue; // 이번 달 이미 실제 거래 존재 → 추정 생략
    const day = inferRepeatDay(rp, records);
    if (!isFuture(day)) continue;
    markers.push({ day, label: `${rp.name} (추정)`, amount: rp.money, kind: 'repeat' });
  }

  const days = [...dayMap.values()].sort((a, b) => a.day - b.day);
  return {
    days,
    markers,
    totals: { income: totalIncome, expense: totalExpense, net: totalIncome - totalExpense },
  };
}
